import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Phone, Mail, MapPin, Trash2, Pencil, X, IdCard } from "lucide-react";

const EMPTY = { name: "", phone: "", email: "", address: "", tax_id: "", notes: "" };

export default function Clients() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/clients").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const startCreate = () => {
    setEditingId(null);
    setForm(EMPTY);
    setShowForm(true);
  };

  const startEdit = (c) => {
    setEditingId(c.id);
    setForm({
      name: c.name || "", phone: c.phone || "", email: c.email || "",
      address: c.address || "", tax_id: c.tax_id || "", notes: c.notes || "",
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancel = () => { setShowForm(false); setEditingId(null); setForm(EMPTY); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name) return toast.error("Nombre requerido");
    if (editingId) {
      const { data } = await api.put(`/clients/${editingId}`, form);
      const n = data?.propagated_budgets || 0;
      toast.success(
        n > 0
          ? `Cliente actualizado · aplicado a ${n} presupuesto${n === 1 ? "" : "s"} en curso`
          : "Cliente actualizado"
      );
    } else {
      await api.post("/clients", form);
      toast.success("Cliente añadido");
    }
    cancel();
    load();
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar cliente?")) return;
    await api.delete(`/clients/${id}`);
    load();
  };

  return (
    <div data-testid="clients-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="kbar">Clientes</div>
          <h1 className="font-display font-extrabold text-3xl sm:text-5xl tracking-tight mt-2">Cartera</h1>
        </div>
        <button onClick={showForm ? cancel : startCreate} data-testid="clients-add" className="btn-brand h-12 px-6 inline-flex items-center gap-2">
          {showForm ? <><X className="w-4 h-4" /> Cerrar</> : <><Plus className="w-4 h-4" /> Nuevo cliente</>}
        </button>
      </div>

      {showForm && (
        <form onSubmit={save} className="mt-6 border border-slate-200 p-6 grid grid-cols-1 sm:grid-cols-2 gap-4 bg-white" data-testid="client-form">
          <div className="sm:col-span-2 -mb-1">
            <div className="kbar">{editingId ? "Editar cliente" : "Nuevo cliente"}</div>
            {editingId && (
              <p className="text-xs text-slate-500 mt-1">
                Los cambios se aplicarán automáticamente a los presupuestos en curso de este cliente (no a facturas ya emitidas).
              </p>
            )}
          </div>
          <input required placeholder="Nombre" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 px-3 border border-slate-200 outline-none focus:border-orange-600" data-testid="client-name" />
          <input placeholder="NIF / CIF" value={form.tax_id} onChange={(e) => setForm({ ...form, tax_id: e.target.value })} className="h-11 px-3 border border-slate-200 outline-none focus:border-orange-600" data-testid="client-tax-id" />
          <input placeholder="Teléfono" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-11 px-3 border border-slate-200 outline-none focus:border-orange-600" data-testid="client-phone" />
          <input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11 px-3 border border-slate-200 outline-none focus:border-orange-600" data-testid="client-email" />
          <input placeholder="Dirección" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="sm:col-span-2 h-11 px-3 border border-slate-200 outline-none focus:border-orange-600" data-testid="client-address" />
          <textarea placeholder="Notas" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="sm:col-span-2 p-3 border border-slate-200 outline-none focus:border-orange-600 resize-none" data-testid="client-notes" />
          <div className="sm:col-span-2 flex items-center gap-3">
            <button data-testid="client-save" className="btn-brand h-11 px-6">{editingId ? "Guardar cambios" : "Guardar"}</button>
            <button type="button" onClick={cancel} className="h-11 px-6 border border-slate-200 hover:border-slate-300 text-sm font-semibold" data-testid="client-cancel">Cancelar</button>
          </div>
        </form>
      )}

      <div className="border border-slate-200 mt-6 bg-white divide-y divide-slate-100">
        {items.length === 0 && <div className="p-12 text-center text-slate-500">Aún no tienes clientes.</div>}
        {items.map((c) => (
          <div key={c.id} className="p-5 flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="font-bold text-lg">{c.name}</div>
              <div className="mt-1 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-600">
                {c.tax_id && <span className="inline-flex items-center gap-1.5"><IdCard className="w-3.5 h-3.5" /> {c.tax_id}</span>}
                {c.phone && <span className="inline-flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> {c.phone}</span>}
                {c.email && <span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" /> {c.email}</span>}
                {c.address && <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {c.address}</span>}
              </div>
              {c.notes && <div className="text-sm text-slate-500 mt-1">{c.notes}</div>}
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => startEdit(c)} data-testid={`edit-client-${c.id}`} className="text-slate-400 hover:text-orange-600 p-2" title="Editar"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => del(c.id)} data-testid={`del-client-${c.id}`} className="text-slate-400 hover:text-red-600 p-2" title="Eliminar"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
